import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@rudi/api/react';
import { Button } from '@rudi/ui/button';
import {
  ClinicalEditorsProvider,
  type ClinicalEditorApi,
} from '@rudi/ui/clinical-trials/context.tsx';
import { createClinicalOperations } from '@rudi/ui/clinical-trials/operations.ts';
import { defaultClinicalText } from '@rudi/ui/clinical-trials/text.ts';
import { ProfileTab } from '@rudi/ui/clinical-trials/ProfileTab.tsx';
import { PosterTab } from '@rudi/ui/clinical-trials/PosterTab.tsx';
import { StudyIntakeDialog } from '@rudi/ui/clinical-trials/StudyIntakeDialog.tsx';
import { StudyManagementDialog } from '@rudi/ui/clinical-trials/StudyManagementDialog.tsx';
import type {
  ManagedClinicalTrial,
  PosterDraft,
  PosterSubmissionResponse,
  ProfileEditRequest,
  PublicClinicSummary,
  PublicInvestigatorDetail,
  PublicStudySummary,
} from '@rudi/ui/clinical-trials/types.ts';
import { toast } from 'sonner';
import { RecruitmentCampaignsPanel } from './RecruitmentCampaignsPanel';

export const teamTabs = [
  'overview',
  'studies',
  'profile',
  'posters',
  'marketing',
  'members',
  'practices',
  'chat',
] as const;
export type TeamTab = (typeof teamTabs)[number];
export interface TeamWorkspaceSearch {
  tab?: TeamTab;
  pi?: string;
  study?: string;
}
export function validateTeamWorkspaceSearch(search: Record<string, unknown>): TeamWorkspaceSearch {
  return {
    tab: teamTabs.includes(search.tab as TeamTab) ? (search.tab as TeamTab) : undefined,
    pi: typeof search.pi === 'string' ? search.pi : undefined,
    study: typeof search.study === 'string' ? search.study : undefined,
  };
}
interface Investigator {
  slug: string;
  fullName: string;
}
export interface TeamWorkspaceData {
  teamId: string;
  tenantId: string;
  status: string;
  investigators: Investigator[];
  allowedActions: string[];
  canProcess: boolean;
  practiceCount: number;
  generatedAt: string;
  profileRequests: (ProfileEditRequest & { submittedBy: string })[];
  posterSubmissions: PosterSubmissionResponse[];
  posterDrafts: PosterDraft[];
  posterEvents?: { submissionId: string; actorId: string; status: string; createdAt: string }[];
}
interface StudyRow {
  id: string;
  study: ManagedClinicalTrial & PublicStudySummary;
  investigators: (Investigator & {
    management: ManagedClinicalTrial['management'];
    study?: ManagedClinicalTrial;
  })[];
}
interface Metrics {
  mediaSpendCents: number | null;
  impressions: number | null;
  clicks: number | null;
  deliveredContactReferrals: number;
  costPerClickCents: number | null;
  costPerDeliveredReferralCents: number | null;
}
interface CampaignReport {
  campaignId: string;
  name: string;
  spansOtherTeams: boolean;
  campaignMetrics: Metrics;
  teamDeliveredContactReferrals: number;
  sources: {
    channel: string;
    spendAsOf: string | null;
    metricsAsOf: string | null;
    spendComplete: boolean;
    metricsComplete: boolean;
    stale: boolean;
  }[];
  readiness: { checkedAt: string; checks: { key: string; ready: boolean; reason: string }[] };
}
export interface TeamReporting {
  generatedAt: string;
  period: string;
  campaigns: CampaignReport[];
}
export function useTeamWorkspace(tenantId: string, teamId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['team-workspace', tenantId, teamId],
    retry: false,
    queryFn: async () =>
      (
        await api.get<TeamWorkspaceData>(
          `/cortex/clinical-trials/tenants/${tenantId}/teams/${teamId}/workspace`
        )
      ).data,
  });
}
function status(error: unknown) {
  return (error as { response?: { status?: number } })?.response?.status;
}
const count = (value: number | null) => (value === null ? 'Unavailable' : value.toLocaleString());
const money = (value: number | null) =>
  value === null
    ? 'Unavailable'
    : new Intl.NumberFormat('en', { style: 'currency', currency: 'EUR' }).format(value / 100);
const recruiting = (study: StudyRow['study']) =>
  ['recruiting', 'recruitment_ongoing', 'ongoing'].includes(
    (
      study.effective_recruitment_status_code ??
      study.recruitment_status_code ??
      study.germany_recruitment_status ??
      ''
    ).toLowerCase()
  );

export function TeamWorkspacePanel({
  tenantId,
  teamId,
  data,
  search,
  navigate,
}: {
  tenantId: string;
  teamId: string;
  data: TeamWorkspaceData;
  search: TeamWorkspaceSearch;
  navigate: (update: Partial<TeamWorkspaceSearch>) => void;
}) {
  const api = useApiClient();
  const qc = useQueryClient();
  const base = `/cortex/clinical-trials/tenants/${tenantId}/teams/${teamId}`;
  const tab = search.tab ?? 'overview';
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignment, setAssignment] = useState('');
  const [assignmentError, setAssignmentError] = useState('');
  const selectedPi = data.investigators.find(pi => pi.slug === search.pi);
  const studies = useQuery({
    queryKey: ['team-workspace', tenantId, teamId, 'studies'],
    retry: false,
    queryFn: async () => (await api.get<StudyRow[]>(`${base}/studies`)).data,
    enabled: ['overview', 'studies', 'marketing'].includes(tab),
  });
  const capabilities = useQuery({
    queryKey: ['team-workspace', tenantId, teamId, 'catalog-capabilities'],
    retry: false,
    queryFn: async () =>
      (
        await api.get<{
          canCreateStudies: boolean;
          canAssignStudies: boolean;
          delegatedManagement: boolean;
        }>(`${base}/catalog-capabilities`)
      ).data,
    enabled: tab === 'studies' && data.status === 'active',
  });
  const available = useQuery({
    queryKey: ['team-workspace', tenantId, teamId, 'available-studies'],
    retry: false,
    queryFn: async () => (await api.get<PublicStudySummary[]>(`${base}/available-studies`)).data,
    enabled: assigning,
  });
  const reporting = useQuery({
    queryKey: ['team-workspace', tenantId, teamId, 'reporting'],
    retry: false,
    queryFn: async () => (await api.get<TeamReporting>(`${base}/marketing/reporting`)).data,
    enabled: ['overview', 'marketing'].includes(tab),
  });
  const editorValue = useMemo(() => {
    const path = (url: string) => {
      if (url === '/clinical-trials/profile-edit-request/me/photo')
        return `${base}/investigators/${encodeURIComponent(search.pi ?? '')}/profile-photo`;
      return url
        .replace('/clinical-trials', base)
        .replace('/public-investigators/', '/investigators/')
        .replace('/public-clinics', '/clinics');
    };
    const transport: ClinicalEditorApi = {
      get: (url, options) => api.get(path(url), options),
      post: async (url, body, options) => {
        const result = await api.post(path(url), body, options);
        void qc.invalidateQueries({ queryKey: ['team-workspace', tenantId, teamId] });
        return result;
      },
      put: async (url, body) => {
        const result = await api.put(path(url), body);
        void qc.invalidateQueries({ queryKey: ['team-workspace', tenantId, teamId] });
        return result;
      },
      patch: (url, body) => api.patch(path(url), body),
      delete: url => api.delete(path(url)),
    };
    return {
      api: transport,
      t: defaultClinicalText,
      profileOrigin: 'https://studien-netzwerk.de',
      operations: createClinicalOperations(transport),
      getPublicInvestigator: async (slug: string) =>
        (
          await api.get<PublicInvestigatorDetail>(
            `${base}/investigators/${encodeURIComponent(slug)}`
          )
        ).data,
      listPublicClinics: async () => (await api.get<PublicClinicSummary[]>(`${base}/clinics`)).data,
      toast: ({ title }: { title: string }) => {
        toast.success(title);
      },
    };
  }, [api, base, qc, search.pi, teamId, tenantId]);
  const selectedStudy = studies.data?.find(row => row.id === search.study);
  const association = selectedStudy?.investigators.find(pi => pi.slug === search.pi);
  const writable = data.allowedActions.length > 0;
  return (
    <ClinicalEditorsProvider value={editorValue}>
      <div className="space-y-4" data-testid="team-workspace">
        {data.status !== 'active' && (
          <p role="status">
            This team is archived. Its local history remains available. Restore it before making
            clinical changes.
          </p>
        )}
        {!data.investigators.length && (
          <p role="status">
            No active principal investigator is linked.{' '}
            <Button variant="link" onClick={() => navigate({ tab: 'members' })}>
              Manage members and PI links
            </Button>
          </p>
        )}
        {data.investigators.length > 0 && (
          <label className="flex items-center gap-3 text-sm">
            Principal investigator
            <select
              className="rounded-3xl border bg-background p-2"
              aria-label="Principal investigator"
              value={search.pi ?? ''}
              onChange={event => navigate({ pi: event.target.value || undefined })}
            >
              <option value="">Select a PI</option>
              {data.investigators.map(pi => (
                <option key={pi.slug} value={pi.slug}>
                  {studies.data
                    ?.flatMap(row => row.investigators)
                    .find(item => item.slug === pi.slug)?.fullName ?? pi.fullName}
                </option>
              ))}
            </select>
          </label>
        )}
        {tab === 'overview' && (
          <>
            <div className="grid gap-4 sm:grid-cols-3">
              <OverviewCard
                title="Distinct studies"
                value={studies.data ? String(studies.data.length) : 'Unavailable'}
                onClick={() => navigate({ tab: 'studies' })}
              />
              <OverviewCard
                title="Recruiting studies"
                value={
                  studies.data
                    ? String(studies.data.filter(row => recruiting(row.study)).length)
                    : 'Unavailable'
                }
                onClick={() => navigate({ tab: 'studies' })}
              />
              <OverviewCard
                title="Outpatient practices"
                value={String(data.practiceCount)}
                onClick={() => navigate({ tab: 'practices' })}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Local records checked {new Date(data.generatedAt).toLocaleString()}.
            </p>
            {studies.isError && (
              <p role="alert">
                The study catalog could not be loaded.{' '}
                <Button variant="outline" onClick={() => void studies.refetch()}>
                  Retry catalog
                </Button>
              </p>
            )}
            <div className="grid gap-4 md:grid-cols-2">
              {data.investigators.map(pi => {
                const profile = data.profileRequests.find(row => row.slug === pi.slug);
                const poster = data.posterSubmissions.find(
                  row => row.selectedInvestigatorSlug === pi.slug
                );
                const draft = data.posterDrafts.find(row => row.piSlug === pi.slug);
                return (
                  <article key={pi.slug} className="space-y-2 rounded-3xl border p-4">
                    <h3 className="font-medium">{pi.fullName}</h3>
                    <p>
                      Website profile:{' '}
                      {profile ? `Changes ${profile.status}` : 'No submitted changes'}
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => navigate({ tab: 'profile', pi: pi.slug })}
                    >
                      Website profile
                    </Button>
                    <p>
                      Poster:{' '}
                      {poster
                        ? `Submission ${poster.status}`
                        : draft
                          ? `Draft version ${draft.version}, not submitted`
                          : 'No submitted poster'}
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => navigate({ tab: 'posters', pi: pi.slug })}
                    >
                      Open posters
                    </Button>
                  </article>
                );
              })}
            </div>
            <Button variant="outline" onClick={() => navigate({ tab: 'marketing' })}>
              Digital marketing
            </Button>
          </>
        )}
        {tab === 'studies' && (
          <>
            {capabilities.error && (
              <p role="alert">
                Could not check catalog editing support.{' '}
                <Button variant="outline" onClick={() => void capabilities.refetch()}>
                  Retry capabilities
                </Button>
              </p>
            )}
            <div className="flex gap-3">
              {capabilities.data?.canCreateStudies && writable && (
                <Button disabled={!selectedPi} onClick={() => setCreating(true)}>
                  Add study
                </Button>
              )}
              {capabilities.data?.canAssignStudies && writable && (
                <Button
                  disabled={!selectedPi}
                  variant="outline"
                  onClick={() => setAssigning(value => !value)}
                >
                  Assign existing study
                </Button>
              )}
            </div>
            {creating && selectedPi && (
              <StudyIntakeDialog
                open
                investigatorSlug={selectedPi.slug}
                onOpenChange={setCreating}
                onComplete={() => void studies.refetch()}
              />
            )}
            {assigning && selectedPi && (
              <div className="space-y-2 rounded-3xl border p-4">
                <label>
                  Study to assign
                  <select
                    aria-label="Study to assign"
                    value={assignment}
                    onChange={event => setAssignment(event.target.value)}
                    className="ml-3 rounded-3xl border p-2"
                  >
                    <option value="">Select a study</option>
                    {available.data
                      ?.filter(row => row.catalog_id)
                      .map(row => (
                        <option key={row.id} value={row.catalog_id!}>
                          {row.title}
                        </option>
                      ))}
                  </select>
                </label>
                {available.error && <p role="alert">Could not load available studies.</p>}
                {assignmentError && <p role="alert">{assignmentError}</p>}
                <Button
                  disabled={!assignment}
                  onClick={() => {
                    void editorValue.operations
                      .assignStudyToInvestigator(selectedPi.slug, assignment)
                      .then(() => {
                        setAssigning(false);
                        return studies.refetch();
                      })
                      .catch(error =>
                        setAssignmentError(
                          error instanceof Error ? error.message : 'Assignment failed.'
                        )
                      );
                  }}
                >
                  Assign to selected PI
                </Button>
              </div>
            )}

            {studies.isLoading && <p>Loading team studies…</p>}
            {studies.isError && (
              <p role="alert">
                The study catalog could not be loaded.{' '}
                <Button onClick={() => void studies.refetch()}>Retry catalog</Button>
              </p>
            )}
            {studies.data && (
              <div className="overflow-x-auto rounded-3xl border">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr>
                      <th className="p-3">Study</th>
                      <th>PI associations</th>
                      <th>Recruitment</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studies.data.map(row => (
                      <tr key={row.id} className="border-t">
                        <td className="p-3">
                          {row.study.title_override ?? row.study.title ?? row.id}
                        </td>
                        <td>{row.investigators.map(pi => pi.fullName).join(', ')}</td>
                        <td>
                          {row.study.effective_recruitment_status_code ??
                            row.study.recruitment_status_code ??
                            'Unavailable'}
                        </td>
                        <td>
                          <Button
                            variant="outline"
                            onClick={() =>
                              navigate({
                                study: row.id,
                                pi:
                                  row.investigators.length === 1
                                    ? row.investigators[0].slug
                                    : search.pi,
                              })
                            }
                          >
                            Open study
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {selectedStudy && !association && (
              <p role="status">
                Select a PI associated with this study:{' '}
                {selectedStudy.investigators.map(pi => (
                  <Button key={pi.slug} variant="outline" onClick={() => navigate({ pi: pi.slug })}>
                    {pi.fullName}
                  </Button>
                ))}
              </p>
            )}
            {selectedStudy &&
              association &&
              (!writable ||
                !capabilities.data?.delegatedManagement ||
                !association.management.recruitment_site_ids) && (
                <section className="space-y-2 rounded-3xl border p-4" aria-label="Selected study">
                  <h3 className="font-medium">{selectedStudy.study.title ?? selectedStudy.id}</h3>
                  <p>{selectedStudy.id}</p>
                  <p className="text-sm text-muted-foreground">
                    Editing is unavailable for this team's selected PI association.
                  </p>
                  <Button variant="outline" onClick={() => navigate({ study: undefined })}>
                    Close study
                  </Button>
                </section>
              )}
            {selectedStudy &&
              association &&
              writable &&
              capabilities.data?.delegatedManagement &&
              association.management.recruitment_site_ids && (
                <StudyManagementDialog
                  key={`${selectedStudy.id}:${association.slug}`}
                  study={{
                    ...(association.study ?? selectedStudy.study),
                    management: association.management,
                  }}
                  open
                  onOpenChange={open => {
                    if (!open) navigate({ study: undefined });
                  }}
                  managementInvestigatorSlug={association.slug}
                  onSaved={async () => {
                    await studies.refetch();
                  }}
                />
              )}
            {studies.data?.length === 0 && (
              <p>No studies are associated with this team's active PIs.</p>
            )}
          </>
        )}
        {(tab === 'profile' || tab === 'posters') &&
          !selectedPi &&
          data.investigators.length > 0 && <p>Select a PI to open this editor.</p>}
        {tab === 'profile' && selectedPi && (
          <ProfileTab
            key={selectedPi.slug}
            viewerSlug={selectedPi.slug}
            viewerLabel={selectedPi.fullName}
            canEdit={writable}
            canSubmitChanges={writable}
            operatorMode
          />
        )}
        {tab === 'posters' && selectedPi && (
          <div className="min-h-[700px]">
            <PosterTab
              key={selectedPi.slug}
              viewerSlug={selectedPi.slug}
              viewerLabel={selectedPi.fullName}
              canSubmit={writable}
              operatorMode
            />
          </div>
        )}
        {tab === 'posters' && (
          <section aria-label="Poster submission history" className="space-y-2">
            <h3 className="font-medium">Submitted versions</h3>
            {data.posterSubmissions
              .filter(row => !search.pi || row.selectedInvestigatorSlug === search.pi)
              .map(row => (
                <article key={row.id} className="rounded-3xl border p-3">
                  <p>
                    {row.fields.header} · {row.status} · {new Date(row.createdAt).toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Submission {row.id}. Further edits save a separate draft.
                  </p>
                  {data.posterEvents
                    ?.filter(event => event.submissionId === row.id)
                    .map(event => (
                      <p key={`${event.createdAt}:${event.status}`} className="text-xs">
                        {event.status} · {new Date(event.createdAt).toLocaleString()} ·{' '}
                        {event.actorId}
                      </p>
                    ))}
                  {row.fields.exportedPdfDataUrl && (
                    <a
                      className="underline"
                      href={row.fields.exportedPdfDataUrl}
                      download={`poster-${row.id}.pdf`}
                    >
                      Download submitted PDF
                    </a>
                  )}
                </article>
              ))}
          </section>
        )}
        {tab === 'profile' && !selectedPi && (
          <section aria-label="Profile history">
            {data.profileRequests.map(row => (
              <article key={row.id} className="rounded-3xl border p-3">
                {row.slug} · {row.status} · {new Date(row.submittedAt).toLocaleString()}
                <p>{row.bio}</p>
              </article>
            ))}
          </section>
        )}
        {['overview', 'marketing'].includes(tab) && (
          <>
            {reporting.isLoading && <p>Loading campaign reporting…</p>}
            {reporting.isError && (
              <p role="alert">
                Campaign reporting could not be loaded.{' '}
                <Button variant="outline" onClick={() => void reporting.refetch()}>
                  Retry reporting
                </Button>
              </p>
            )}
            {reporting.data && <CampaignReporting data={reporting.data} />}
          </>
        )}
        {tab === 'marketing' && (
          <>
            <p className="text-sm text-muted-foreground">
              Prepare campaign drafts and materials here. Customer acceptance and funding remain
              with the customer.
            </p>
            <RecruitmentCampaignsPanel teamBase={base} />
          </>
        )}
      </div>
    </ClinicalEditorsProvider>
  );
}
function OverviewCard({
  title,
  value,
  onClick,
}: {
  title: string;
  value: string;
  onClick: () => void;
}) {
  return (
    <button
      aria-label={`${value} ${title}`}
      className="rounded-3xl border p-5 text-left hover:bg-muted"
      onClick={onClick}
    >
      <span className="block text-2xl font-semibold">{value}</span>
      {title}
    </button>
  );
}
function CampaignReporting({ data }: { data: TeamReporting }) {
  return (
    <section aria-label="Campaign reporting" className="space-y-4">
      <h3 className="font-medium">Lifetime campaign reporting</h3>
      <p className="text-xs text-muted-foreground">
        Checked {new Date(data.generatedAt).toLocaleString()}. Campaign totals include all
        participating teams and Sites.
      </p>
      {data.campaigns.length === 0 && (
        <p>No campaigns match this team's authorized study and Site associations.</p>
      )}
      {data.campaigns.map(row => (
        <article key={row.campaignId} className="space-y-3 rounded-3xl border p-4">
          <h4 className="font-medium">{row.name}</h4>
          <p>Team-attributable delivered contact referrals: {row.teamDeliveredContactReferrals}</p>
          <p className="text-sm">
            {row.spansOtherTeams ? 'This campaign spans other teams or Sites. ' : ''}The following
            metrics cover the whole campaign.
          </p>
          <dl className="grid gap-3 sm:grid-cols-3">
            {[
              ['Campaign-wide spend', money(row.campaignMetrics.mediaSpendCents)],
              ['Impressions', count(row.campaignMetrics.impressions)],
              ['Clicks', count(row.campaignMetrics.clicks)],
              [
                'Campaign-wide delivered contact referrals',
                count(row.campaignMetrics.deliveredContactReferrals),
              ],
              ['Cost per click', money(row.campaignMetrics.costPerClickCents)],
              [
                'Campaign-wide cost per delivered referral',
                money(row.campaignMetrics.costPerDeliveredReferralCents),
              ],
            ].map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs text-muted-foreground">{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
          {row.sources.map((source, index) => (
            <p className="text-xs text-muted-foreground" key={`${source.channel}:${index}`}>
              {source.channel}: spend as of {source.spendAsOf ?? 'unavailable'}, metrics as of{' '}
              {source.metricsAsOf ?? 'unavailable'}.{' '}
              {!source.spendComplete || !source.metricsComplete ? 'Incomplete data. ' : ''}
              {source.stale ? 'Source data is stale or missing.' : ''}
            </p>
          ))}
          <p className="text-xs">
            Readiness checked {new Date(row.readiness.checkedAt).toLocaleString()}.
          </p>
          <ul className="list-disc pl-5 text-sm">
            {row.readiness.checks
              .filter(check => !check.ready)
              .map(check => (
                <li key={check.key}>{check.reason}</li>
              ))}
          </ul>
        </article>
      ))}
    </section>
  );
}
export function WorkspaceUnavailable({ error, retry }: { error: unknown; retry: () => void }) {
  return (
    <p role="alert">
      {[404, 405].includes(status(error) ?? 0)
        ? 'The team workspace is unavailable on this backend. Members and outpatient practices are still available.'
        : 'Could not load the team workspace.'}{' '}
      <Button variant="outline" onClick={retry}>
        Retry workspace
      </Button>
    </p>
  );
}
